import React, { useState, useMemo } from "react";
import { DataTable, Badge, Pagination } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { Eye, Edit2, UserX, BellOff } from "lucide-react";

export const ReceivedAmountTable = ({ receipts, onView, onEdit, onToggleDisable, onStopBlink }) => {
  const [page, setPage] = useState(1);
  const limit = 10;

  const columns = [
    // {
    //   header: "Receipt ID",
    //   accessor: (row) => (
    //     <div className="text-brand-orange font-bold !text-[10px] flex items-center py-2 uppercase tracking-tight whitespace-nowrap">
    //       #{row.receiptId || "—"}
    //     </div>
    //   ),
    //   minWidth: "110px",
    // },
    {
      header: "Verve ID",
      accessor: (row) => (
        <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
          {row.project?.customId || row.project?.customerCustomId || row.project?.projectId || row.projectId || "—"}
        </span>
      ),
      minWidth: "100px",
    },
    {
      header: 'Customer Info',
      accessor: (row) => {
        const p = row.project || {};
        const fullName = p.customerName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || row.clientName || 'No Name';
        const phone = p.phoneNumber || p.phone || '—';
        const email = p.email || '';
        const contactStr = email ? `${phone} | ${email}` : phone;
        
        return (
          <div className="flex flex-col min-w-0" title={`${fullName}\n${contactStr}`}>
            <span className="text-brand-dark tracking-tight text-sm truncate flex items-center gap-2" title={fullName}>
              {fullName}
              {row.isFollowup && !row.isBlinkingStopped && (
                <div className="flex items-center gap-1 text-red-500 font-bold text-[10px] animate-pulse uppercase tracking-wider">
                  <span>Follow Up Due</span>
                </div>
              )}
            </span>
            <span className="text-[10px] text-slate-400 truncate mt-0.5" title={contactStr}>
              {phone}{email && <span className="mx-1 text-slate-300">|</span>}{email}
            </span>
          </div>
        );
      },
      minWidth: '200px',
    },
    {
      header: 'Address',
      accessor: (row) => {
        const p = row.project || {};
        const parts = [
          p.addressLine1 || p.address1,
          p.addressLine2 || p.address2,
          p.city,
          p.district,
          p.state,
          p.pincode
        ].filter(Boolean);
        const addressText = parts.length > 0 ? parts.join(", ") : '—';
        return (
          <div className="flex flex-col min-w-0" title={addressText}>
            <span className="text-slate-600 text-[11px] truncate">{addressText}</span>
          </div>
        );
      },
      minWidth: '200px',
    },

    {
      header: "Payment Stage",
      accessor: (row) => (
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-indigo-700 bg-indigo-50 border border-indigo-100 whitespace-nowrap">
          {row.paymentStage || "—"}
        </span>
      ),
      minWidth: "120px",
    },
    {
      header: "Payment Date",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] flex items-center whitespace-nowrap">
          {row.paymentDate ? fmtDate(row.paymentDate) : "—"}
        </div>
      ),
      minWidth: "100px",
    },
    {
      header: "Reason (Follow-up)",
      accessor: (row) => {
        const text = row.isFollowup ? row.followupReason || "—" : "—";
        return (
          <div className="text-slate-600 !text-[10px] whitespace-nowrap truncate max-w-[120px]" title={text}>
            {text}
          </div>
        );
      },
      minWidth: "120px",
    },
    {
      header: "Approx Date",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] whitespace-nowrap">
          {row.isFollowup && row.approxPaymentDate ? fmtDate(row.approxPaymentDate) : "—"}
        </div>
      ),
      minWidth: "100px",
    },
    {
      header: "Payment Mode",
      accessor: (row) => (
        <div className="text-slate-500 !text-[10px] font-medium flex items-center capitalize whitespace-nowrap">
          {row.paymentMode?.replace(/_/g, " ") || "—"}
        </div>
      ),
      minWidth: "110px",
    },
    {
      header: "Amount Received",
      accessor: (row) => (
        <div className="text-emerald-500 !text-[10px] font-bold flex items-center whitespace-nowrap">
          {fmt(row.amountReceived || 0)}
        </div>
      ),
      minWidth: "120px",
    },
    {
      header: "Balance Amount",
      accessor: (row) => (
        <div className="text-rose-500 !text-[10px] font-bold flex items-center whitespace-nowrap">
          {fmt(row.balanceAmount || 0)}
        </div>
      ),
      minWidth: "120px",
    },
    {
      header: "Actions",
      accessor: (row) => {
        const isCancelled = row.status === 'cancelled';
        return (
          <div className="flex items-center justify-center whitespace-nowrap">
            <button
              onClick={(e) => { e.stopPropagation(); onView && onView(row); }}
              className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
              title="View Detail"
            >
              <Eye size={16} />
            </button>
            {row.isFollowup && !row.isBlinkingStopped && onStopBlink && (
              <button
                onClick={(e) => { e.stopPropagation(); onStopBlink(row); }}
                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all ml-1"
                title="Stop Blinking / Acknowledge"
              >
                <BellOff size={16} />
              </button>
            )}
          </div>
        );
      },
      minWidth: "60px",
    }
  ];

  const getRowClassName = (row) => {
    let classes = "";
    if (row.status === 'cancelled') {
      classes += "opacity-50 grayscale saturate-0 ";
    }
    return classes.trim();
  };

  const safeReceipts = receipts || [];
  const totalPages = Math.ceil(safeReceipts.length / limit);
  const paginatedReceipts = useMemo(() => {
    const start = (page - 1) * limit;
    return safeReceipts.slice(start, start + limit);
  }, [safeReceipts, page]);

  // Handle case where items are deleted and page goes out of bounds
  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-700 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col">
      <DataTable 
        columns={columns} 
        data={paginatedReceipts} 
        getRowClassName={getRowClassName}
      />
      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};
